/** @format */

export function cloneDeep<T>(source: T): T {
	if (typeof source !== 'object' || source === null) {
		return source;
	}

	const clonedObj: any = Array.isArray(source) ? [] : {};

	for (const key in source) {
		if (Object.prototype.hasOwnProperty.call(source, key)) {
			clonedObj[key] = cloneDeep(source[key]);
		}
	}

	return clonedObj;
}

export function defaultsDeep(target: object, ...sources: object[]): object {
	sources.forEach((source) => {
		if (source == null) return;
		Object.keys(source).forEach((key) => {
			const targetValue = target[key];
			const sourceValue = source[key];

			if (typeof sourceValue === 'object' && sourceValue !== null) {
				if (typeof targetValue === 'object' && targetValue !== null) {
					target[key] = defaultsDeep(targetValue, sourceValue);
				} else {
					target[key] = defaultsDeep({}, sourceValue);
				}
			} else if (target[key] === undefined) {
				target[key] = sourceValue;
			}
		});
	});

	return target;
}

export function get<T>(obj: any, path: string, defaultValue?: T): T | undefined {
	const keys = path.split('.');
	let result: any = obj;

	for (const key of keys) {
		if (result && typeof result === 'object' && key in result) {
			result = result[key];
		} else {
			return defaultValue;
		}
	}

	return result;
}

export function set(obj: any, path: string, value: any): void {
	const keys = path.split('.');
	let target = obj;

	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		if (i === keys.length - 1) {
			target[key] = value;
		} else {
			if (!target[key] || typeof target[key] !== 'object') {
				target[key] = {};
			}
			target = target[key];
		}
	}
}

type Mergeable = Record<string, any>;

function isObject(obj: any): obj is Mergeable {
	return obj !== null && typeof obj === 'object';
}

export function merge(target: Mergeable, ...sources: Mergeable[]): Mergeable {
	sources.forEach((source) => {
		Object.keys(source).forEach((key) => {
			const targetValue = target[key];
			const sourceValue = source[key];

			if (isObject(targetValue) && isObject(sourceValue)) {
				target[key] = merge(targetValue, sourceValue);
			} else {
				target[key] = sourceValue;
			}
		});
	});

	return target;
}

export function snakeCase(input: string): string {
	return input
		.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
		.replace(/\s+/g, '_')
		.replace(/-+/g, '_')
		.replace(/[^a-zA-Z0-9_]/g, '')
		.replace(/^_+|_+$/g, '')
		.toLowerCase();
}
